
import React, { useState, useEffect } from 'react';
import { QuoteState, User as UserType } from '../types';
import { apiService } from '../services/apiService';
import { Save, FolderOpen, Trash2, X, FileText, Clock, Plus, Eye, Receipt, Calendar, User, ChevronRight, Loader2 } from 'lucide-react';

interface SavedQuoteMeta {
  id: string;
  name: string;
  clientName: string;
  lastModified: number;
  docNumber: string;
  amount: number;
  currency: string;
}

interface QuoteManagerProps {
  isOpen: boolean;
  onClose: () => void;
  currentQuote: QuoteState;
  onLoadQuote: (quote: QuoteState) => void;
  currentUser: UserType;
  initialMode?: 'list' | 'save';
}

export const QuoteManager: React.FC<QuoteManagerProps> = ({ isOpen, onClose, currentQuote, onLoadQuote, currentUser, initialMode = 'list' }) => {
  const [savedQuotes, setSavedQuotes] = useState<SavedQuoteMeta[]>([]);
  const [saveName, setSaveName] = useState('');
  const [mode, setMode] = useState<'list' | 'save'>('list');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadQuotesList();
      setSaveName(`${currentQuote.client.name || 'Untitled'} - ${currentQuote.details.date}`);
      setMode(initialMode);
    }
  }, [isOpen, currentQuote, initialMode]);

  const loadQuotesList = async () => {
    setIsLoading(true);
    try {
        const data = await apiService.getQuotes();
        setSavedQuotes(data);
    } catch (e) {
        console.error("Error loading quote registry", e);
        setSavedQuotes([]);
    } finally {
        setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!saveName.trim()) return;
    try {
        await apiService.saveQuote(currentQuote, currentQuote.details.currency as any, saveName);
        await loadQuotesList();
        setMode('list');
    } catch (e) {
        alert('Failed to archive quote to registry.');
    }
  };

  const handleLoad = async (id: string) => {
    try {
      const quote = await apiService.getQuoteById(id);
      // Added comment above fix: Accessing the 'data' property which contains the actual QuoteState object to match the parameter type of onLoadQuote
      onLoadQuote(quote.data);
      onClose();
    } catch (e) {
      alert("Failed to retrieve quote document.");
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentUser.role !== 'Admin') {
      alert("Unauthorized: Only system administrators can revoke registry entries.");
      return;
    }

    if (window.confirm('Remove this Proforma from historical records?')) {
      const success = await apiService.deleteQuote(id);
      if (success) {
        setSavedQuotes(prev => prev.filter(q => q.id !== id));
      }
    }
  };

  const formatCurrency = (amount: any, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(Number(amount));
  };

  if (!isOpen) return null;

  const isAdmin = currentUser.role === 'Admin';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-300 flex flex-col max-h-[85vh] relative">
        <header className="p-8 bg-slate-900 text-white flex justify-between items-center shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <FolderOpen size={24} />
            </div>
            <div>
              <h3 className="text-xl font-black uppercase tracking-tight">Proforma Archive</h3>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Global Registry Access</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
            <X size={24} />
          </button>
        </header>

        <div className="p-8 flex-1 overflow-y-auto custom-scrollbar bg-slate-50">
           {mode === 'list' ? (
             <div className="space-y-4">
               <button 
                 onClick={() => setMode('save')}
                 className="w-full py-6 border-2 border-dashed border-indigo-200 rounded-[32px] text-indigo-500 hover:border-indigo-500 hover:bg-indigo-50 transition-all flex flex-col items-center justify-center gap-2 group"
               >
                 <Plus size={24} className="group-hover:scale-110 transition-transform" />
                 <span className="text-[10px] font-black uppercase tracking-[0.3em]">Backup current workspace</span>
               </button>

               {isLoading ? (
                  <div className="py-20 flex flex-col items-center justify-center text-slate-400 gap-4">
                    <Loader2 size={32} className="animate-spin text-indigo-500" />
                    <p className="text-[10px] font-black uppercase tracking-widest">Querying Registry...</p>
                  </div>
               ) : savedQuotes.length === 0 ? (
                 <div className="text-center py-20 bg-white rounded-[32px] border border-slate-100 shadow-inner">
                    <Receipt size={48} className="mx-auto text-slate-100 mb-4" />
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-300">Archive Zero State</p>
                 </div>
               ) : (
                 <div className="grid gap-3">
                   {savedQuotes.map(quote => (
                     <div 
                        key={quote.id} 
                        onClick={() => handleLoad(quote.id)}
                        className="group flex items-center justify-between p-5 rounded-[24px] border border-slate-200 hover:border-indigo-400 hover:shadow-xl cursor-pointer transition-all bg-white relative overflow-hidden"
                     >
                        <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 opacity-20"></div>
                        <div className="flex items-center gap-4 min-w-0">
                           <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                             <FileText size={20} />
                           </div>
                           <div className="min-w-0">
                             <h3 className="text-sm font-black text-slate-800 truncate uppercase tracking-tight">{quote.name}</h3>
                             <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1 truncate">{quote.clientName || 'Unspecified Client'}</p>
                             <div className="flex items-center gap-3 text-[9px] font-black text-slate-300 uppercase mt-2">
                               <span className="flex items-center gap-1"><Clock size={10} /> {new Date(quote.lastModified).toLocaleDateString()}</span>
                               <span className="flex items-center gap-1 text-indigo-400/60"><Receipt size={10} /> {quote.docNumber}</span>
                             </div>
                           </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="text-right hidden sm:block">
                                <p className="text-sm font-black text-slate-900 leading-none">{formatCurrency(quote.amount, quote.currency)}</p>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Audit Total</p>
                            </div>
                            {isAdmin && (
                              <button 
                                  onClick={(e) => handleDelete(quote.id, e)}
                                  className="p-2.5 text-slate-200 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                              >
                                  <Trash2 size={18} />
                              </button>
                            )}
                        </div>
                     </div>
                   ))}
                 </div>
               )}
             </div>
           ) : (
             <div className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-xl animate-in slide-in-from-right-4 duration-300">
                <div className="flex items-center gap-3 mb-6">
                    <Save className="text-indigo-500" />
                    <h4 className="font-black text-slate-800 uppercase tracking-tight">Manual Snapshot</h4>
                </div>
                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Reference Alias</label>
                        <input 
                            type="text" 
                            value={saveName}
                            onChange={(e) => setSaveName(e.target.value)}
                            className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all"
                            placeholder="e.g. Q1 Supply - Arusha Node"
                            autoFocus
                        />
                    </div>
                    <div className="flex gap-3 justify-end pt-4 border-t border-slate-50">
                        <button 
                            onClick={() => setMode('list')}
                            className="px-8 py-3 text-slate-400 font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 rounded-2xl"
                        >
                            Discard
                        </button>
                        <button 
                            onClick={handleSave}
                            disabled={!saveName.trim()}
                            className="px-10 py-3 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 disabled:opacity-50 transition-all active:scale-95"
                        >
                            Save Registry Entry
                        </button>
                    </div>
                </div>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};
